import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArticleCombobox } from "@/pages/SalesOrders";

// P17-UAT-15 REV-D1A · Running Weight transaction popup.
// Single modal drives Create / Edit / View / Authorise per lifecycle.
// Product selection reuses the SO ArticleCombobox (authoritative Product Master);
// product auto-derives description, Weaving BOM standard g/m, QA tolerance & sample
// count. NO free-text product. NO tolerance/sampling config here.

const inp = "h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const fmt = (v, d = 3) => (v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(d));
const TOL_CLS = { WITHIN: "text-emerald-300", OUT: "text-amber-300", UNKNOWN: "text-zinc-400" };
const STATUS_CLS = {
  DRAFT: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  CONFIRMED: "bg-sky-500/15 text-sky-300 border-sky-400/40",
  AUTHORISED: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
};

export function QARunningWeightDialog({ open, onClose, recordId, mode, onSaved }) {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("qa_running_weight:edit");
  const canAuthorise = hasPerm("qa_running_weight:approve");

  const [articles, setArticles] = useState([]);
  const [articleId, setArticleId] = useState("");
  const [productCode, setProductCode] = useState("");
  const [derived, setDerived] = useState(null);
  const [productionDate, setProductionDate] = useState("");
  const [shift, setShift] = useState("");
  const [readings, setReadings] = useState(["", "", "", "", ""]);
  const [rec, setRec] = useState(null);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);

  const isNew = !recordId;
  const status = rec?.status || "DRAFT";
  const authorised = status === "AUTHORISED";
  const isOut = rec?.tolerance_result === "OUT";

  useEffect(() => {
    if (!open) return;
    api.get("/articles/").then(({ data }) => setArticles(data || [])).catch(() => setArticles([]));
  }, [open]);

  const deriveProduct = useCallback(async (pc) => {
    if (!pc) { setDerived(null); return; }
    try {
      const { data } = await api.get(`/qa/product-derived?product_code=${encodeURIComponent(pc)}`);
      setDerived(data);
      if (data.sampling_count && isNew) setReadings(Array(data.sampling_count).fill(""));
    } catch { setDerived(null); }
  }, [isNew]);

  const loadRecord = useCallback(async (rid) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/qa/running-weights/${rid}`);
      setRec(data.record);
      setProductCode(data.record.product_code);
      setProductionDate(data.record.production_date);
      setShift(data.record.shift || "");
      setRemark(data.record.manager_remark || "");
      // REV-B · hydrate the actual persisted observations (dynamic count, preserves order).
      const obs = (data.record.observations || []).map((o) => String(o.value_gpm));
      setReadings(obs.length ? obs : ["", "", "", "", ""]);
      await deriveProduct(data.record.product_code);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load record"); }
    finally { setBusy(false); }
  }, [deriveProduct]);

  useEffect(() => {
    if (!open) return;
    setRec(null); setDerived(null); setArticleId(""); setProductCode(""); setProductionDate("");
    setShift(""); setRemark(""); setReadings(["", "", "", "", ""]);
    if (recordId) loadRecord(recordId);
  }, [open, recordId, loadRecord]);

  const onPickArticle = (id) => {
    setArticleId(id);
    const a = articles.find((x) => x.id === id);
    const pc = a?.product_code || "";
    setProductCode(pc);
    deriveProduct(pc);
  };

  const refresh = async () => { if (rec?.id) await loadRecord(rec.id); onSaved && onSaved(); };

  const saveDraft = async () => {
    const nums = readings.map((r) => parseFloat(r)).filter((v) => !isNaN(v) && v > 0);
    if (!productCode || !productionDate) { toast.error("Select a Product and Production Date"); return; }
    if (nums.length < 1) { toast.error("Enter observations (g/m)"); return; }
    // REV-D1A.1 · block implausible entries client-side with the exact message.
    const bad = nums.find((n) => plausLo != null && (n < plausLo - 1e-9 || n > plausHi + 1e-9));
    if (bad !== undefined) {
      toast.error(`Reading ${bad.toFixed(3)} g/m is outside the permitted entry range ${plausLo.toFixed(3)}–${plausHi.toFixed(3)} g/m. Please verify the entered value.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/qa/running-weights`, {
        id: rec?.id || undefined, product_code: productCode, production_date: productionDate,
        shift, readings: nums,
      });
      toast.success("Draft saved");
      setRec(data.record);
      onSaved && onSaved();
      await loadRecord(data.record.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await api.post(`/qa/running-weights/${rec.id}/${path}`, body || {});
      toast.success(okMsg);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const excludeObs = async (seq) => {
    const reason = window.prompt("Exclusion reason?") || "";
    if (!reason.trim()) return;
    await act("exclude", { seq, reason }, "Observation excluded");
  };

  const title = isNew ? "New Running Weight" : `${status} · ${productCode} · ${productionDate}`;
  const editableDraft = canEdit && (isNew || status === "DRAFT") && mode !== "view";

  // REV-D1A.1 · classify a reading vs BOM standard: plausibility (±20%, BLOCK) and
  // QA acceptance (±tol%, RED highlight but valid). Ranges come from derive (create/
  // draft) or the stored record (view).
  const std = derived?.standard_gpm ?? rec?.standard_gpm ?? null;
  const plausLo = derived?.plausibility_lo ?? (std ? +(std * 0.8).toFixed(3) : null);
  const plausHi = derived?.plausibility_hi ?? (std ? +(std * 1.2).toFixed(3) : null);
  const tolPct = derived?.tolerance_pct ?? rec?.tolerance_pct ?? null;
  const accLo = derived?.acceptance_lo ?? (std && tolPct != null ? +(std * (1 - tolPct / 100)).toFixed(3) : null);
  const accHi = derived?.acceptance_hi ?? (std && tolPct != null ? +(std * (1 + tolPct / 100)).toFixed(3) : null);
  const classify = (v) => {
    const n = parseFloat(v);
    if (isNaN(n) || v === "") return "empty";
    if (plausLo != null && (n < plausLo - 1e-9 || n > plausHi + 1e-9)) return "block";
    if (accLo != null && (n < accLo - 1e-9 || n > accHi + 1e-9)) return "warn";
    return "ok";
  };
  const implausible = readings.filter((r) => classify(r) === "block");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="qa-rw-dialog" description="QA Running Weight transaction — capture, confirm and authorise the daily weight.">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            {isNew ? "New Running Weight" : "Running Weight"}
            {rec && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border uppercase ${STATUS_CLS[status] || ""}`} data-testid="qa-dialog-status">{status}</span>}
          </DialogTitle>
        </DialogHeader>

        {/* Product selection (create only) / display */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-zinc-500">Product Code</label>
            {isNew ? (
              <ArticleCombobox testid="qa-product-picker" articles={articles} value={articleId}
                onChange={onPickArticle} onCreated={(a) => { setArticles((p) => [...p, a]); onPickArticle(a.id); }} />
            ) : (
              <div className="h-9 flex items-center font-mono text-white" data-testid="qa-dialog-product">{productCode}</div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase text-zinc-500">Production Date</label>
            {editableDraft
              ? <input className={inp} type="date" data-testid="qa-dialog-date" value={productionDate} onChange={(e) => setProductionDate(e.target.value)} />
              : <div className="h-9 flex items-center font-mono text-zinc-300">{productionDate}</div>}
          </div>
        </div>

        {/* Derived operator-facing QA specification (read-only, auto-calculated) */}
        {derived && (
          <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-0)]" data-testid="qa-derived">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">QA Specification (interim{derived.tolerance_source === "INTERIM_DEFAULT" ? "" : " · product spec"})</div>
            <div className="mb-3"><span className="text-zinc-500 text-[10px] uppercase block">Description</span><span className="text-white font-semibold" data-testid="qa-derived-desc">{derived.description || "—"}</span></div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div><span className="text-zinc-500 text-[10px] uppercase block">Standard g/m (BOM)</span><span className="font-mono text-white" data-testid="qa-derived-standard">{derived.has_bom ? fmt(derived.standard_gpm, 3) : <span className="text-red-400">no BOM</span>}</span></div>
              <div><span className="text-zinc-500 text-[10px] uppercase block">Tolerance</span><span className="font-mono text-zinc-200" data-testid="qa-derived-tol">±{fmt(derived.tolerance_pct, 2)}%</span></div>
              <div><span className="text-zinc-500 text-[10px] uppercase block">Required Samples</span><span className="font-mono text-zinc-200" data-testid="qa-derived-samples">{derived.sampling_count ?? 5}</span></div>
              <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/5 px-2 py-1"><span className="text-emerald-400/80 text-[10px] uppercase block">Lower Limit</span><span className="font-mono text-emerald-200 text-base" data-testid="qa-derived-lower">{fmt(derived.lower_limit_gpm, 3)} g/m</span></div>
              <div className="rounded-sm border border-emerald-500/40 bg-emerald-500/5 px-2 py-1"><span className="text-emerald-400/80 text-[10px] uppercase block">Upper Limit</span><span className="font-mono text-emerald-200 text-base" data-testid="qa-derived-upper">{fmt(derived.upper_limit_gpm, 3)} g/m</span></div>
              <div><span className="text-zinc-500 text-[10px] uppercase block">BOM Version</span><span className="font-mono text-zinc-400" data-testid="qa-derived-bomver">{derived.bom_version || "—"}</span></div>
            </div>
          </div>
        )}

        {/* Observations */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase text-zinc-500">Observations (g/m)</div>
            {editableDraft && <input className={`${inp} h-8 w-28`} data-testid="qa-dialog-shift" value={shift} onChange={(e) => setShift(e.target.value)} placeholder="Shift" />}
          </div>
          {editableDraft ? (
            <>
              <div className="grid grid-cols-5 gap-2">
                {readings.map((r, i) => {
                  const cls = classify(r);
                  const ring = cls === "block" ? "border-red-500 text-red-300"
                    : cls === "warn" ? "border-amber-500 text-amber-300" : "border-[var(--border)] text-white";
                  return (
                    <input key={i} className={`h-9 px-3 bg-[var(--bg-0)] border rounded-sm text-sm focus:outline-none ${ring}`}
                      type="number" step="0.01" min="0" data-testid={`qa-dialog-obs-${i + 1}`}
                      title={cls === "block" ? "Outside entry plausibility range" : cls === "warn" ? "OUTSIDE ACCEPTANCE RANGE" : ""}
                      placeholder={`R${i + 1}`} value={r}
                      onChange={(e) => setReadings((p) => p.map((x, idx) => (idx === i ? e.target.value : x)))} />
                  );
                })}
              </div>
              {implausible.length > 0 && (
                <div className="mt-2 text-xs text-red-300" data-testid="qa-plausibility-error">
                  {implausible.length} reading(s) outside the permitted entry range {fmt(plausLo, 3)}–{fmt(plausHi, 3)} g/m — save is blocked. Please verify.
                </div>
              )}
              {implausible.length === 0 && accLo != null && readings.some((r) => classify(r) === "warn") && (
                <div className="mt-2 text-xs text-amber-300">Some readings are OUTSIDE ACCEPTANCE RANGE (still valid & saveable).</div>
              )}
            </>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="qa-dialog-observations">
              {(rec?.observations || []).map((o) => {
                const cls = classify(o.value_gpm);
                const base = o.valid
                  ? (cls === "warn" ? "border-amber-500/60 text-amber-300" : "border-emerald-500/40 text-emerald-200")
                  : "border-red-500/40 text-red-300 line-through";
                return (
                  <div key={o.seq} className={`px-2 py-1 rounded-sm border text-xs font-mono ${base}`}
                    title={!o.valid ? `Excluded: ${o.exclusion_reason}` : (cls === "warn" ? "OUTSIDE ACCEPTANCE RANGE" : "")}>
                    R{o.seq}: {fmt(o.value_gpm, 2)}
                    {o.valid && canEdit && status === "CONFIRMED" && !authorised && (
                      <button data-testid={`qa-dialog-exclude-${o.seq}`} onClick={() => excludeObs(o.seq)} className="ml-2 text-red-400 hover:text-red-300">✕</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {editableDraft && <button data-testid="qa-dialog-add-obs" onClick={() => setReadings((p) => [...p, ""])} className="mt-2 h-7 px-3 rounded-sm border border-[var(--border)] text-zinc-300 text-xs">+ Observation</button>}
        </div>

        {/* Computed result */}
        {rec && rec.status !== "DRAFT" && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm border-t border-[var(--border)] pt-3">
            <div><span className="text-zinc-500 text-[10px] uppercase block">Actual g/m</span><span className="font-mono text-white" data-testid="qa-dialog-actual">{fmt(rec.actual_gpm, 3)}</span></div>
            <div><span className="text-zinc-500 text-[10px] uppercase block">Std g/m</span><span className="font-mono text-zinc-300">{fmt(rec.standard_gpm, 3)}</span></div>
            <div><span className="text-zinc-500 text-[10px] uppercase block">Variance %</span><span className="font-mono">{fmt(rec.variance_pct, 2)}</span></div>
            <div><span className="text-zinc-500 text-[10px] uppercase block">Result</span><span className={`font-mono font-bold ${TOL_CLS[rec.tolerance_result] || ""}`} data-testid="qa-dialog-result">{rec.tolerance_result || "—"}</span></div>
            <div><span className="text-zinc-500 text-[10px] uppercase block">QA Manager</span><span className="font-mono text-zinc-300">{rec.authorized_by || "—"}</span></div>
          </div>
        )}
        {rec?.manager_remark && <div className="text-xs"><span className="text-zinc-500 uppercase text-[10px]">Manager Remark: </span><span className="text-amber-200">{rec.manager_remark}</span></div>}

        {/* OOT mandatory remark before authorise */}
        {canAuthorise && status === "CONFIRMED" && isOut && (
          <div>
            <label className="text-[10px] uppercase text-red-300 block mb-1">Investigation Remark (mandatory — OUT OF TOLERANCE)</label>
            <textarea className={`${inp} w-full h-16`} data-testid="qa-dialog-remark" value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="QA Manager investigation / disposition reason" />
          </div>
        )}

        {/* Actions per lifecycle */}
        <div className="flex flex-wrap gap-2 justify-end border-t border-[var(--border)] pt-3">
          {editableDraft && (
            <button data-testid="qa-dialog-save" onClick={saveDraft} disabled={busy}
              className="h-9 px-4 rounded-sm bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-40">Save Draft</button>
          )}
          {canEdit && status === "DRAFT" && rec && (
            <button data-testid="qa-dialog-confirm" onClick={() => act("confirm", {}, "Confirmed")} disabled={busy}
              className="h-9 px-4 rounded-sm bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold disabled:opacity-40">Confirm</button>
          )}
          {canAuthorise && status === "CONFIRMED" && (
            <button data-testid="qa-dialog-authorise" onClick={() => act("authorise", { manager_remark: remark }, "Authorised & frozen")} disabled={busy}
              className="h-9 px-5 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-40">Authorise</button>
          )}
          {canAuthorise && authorised && (
            <button data-testid="qa-dialog-supersede" onClick={() => { if (window.confirm("Supersede this authorised record? Original stays immutable; no WCR/stock change.")) act("supersede", {}, "Superseded"); }} disabled={busy}
              className="h-9 px-4 rounded-sm bg-amber-600/80 hover:bg-amber-500 text-white text-sm font-semibold disabled:opacity-40">Supersede</button>
          )}
          {authorised && <span className="self-center text-xs text-emerald-400" data-testid="qa-dialog-frozen">🔒 Frozen</span>}
          <button data-testid="qa-dialog-close" onClick={onClose} className="h-9 px-4 rounded-sm border border-[var(--border)] text-zinc-400 text-sm">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default QARunningWeightDialog;
